import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import getRosterPlatformUrl from '../../../utils/getRosterPlatformUrl';
import AdvancedDataTable from '../../DataTables/AdvancedDataTable';
import MobileDataTableCard from '../../DataTables/MobileDataTableCard';
import RosterFormModal from '../../Roster/RosterFormModal/RosterFormModal';
import PricingTable from '../../PricingTable/PricingTable';
import FilterByPlatform from '../../Filters/FilterByPlatform';
import FilterByScoringType from '../../Filters/FilterByScoringType';
import FilterByFormat from '../../Filters/FilterByFormat';
import FilterBySearchInput from '../../Filters/FilterBySearchInput';
import ClearFiltersButton from '../../Filters/ClearFilterButton/ClearFiltersButton';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import RostersPlayersExposuresTable from '../RostersPlayersExposuresTable/RostersPlayersExposuresTable';
import RostersPlayersExposuresChart from '../RostersPlayersExposuresChart/RostersPlayersExposuresChart';
import SuccessModal from '../../SuccessModal/SuccessModal';
import Modal from '../../Modal/Modal';

const RostersIndexCard = ({
	get_rosters_by_user_endpoint,
	create_roster_by_user_endpoint,
	update_roster_by_user_endpoint,
	delete_roster_by_user_endpoint,
	get_rosters_players_exposures_by_user_endpoint,
	get_rosters_players_injuries_by_user_endpoint,
	create_checkout_session_endpoint,
	stripe_public_key,
	stripe_product_essential_id,
	stripe_product_advanced_id,
	stripe_product_ultimate_id
}) => {
	const { user } = useAuth();
	const { subscription } = useAuth();
	const navigate = useNavigate();
	const modalRef = useRef();
	const [
		rosters,
		setRosters
	] = useState([]);
	const [
		isCreateRosterModalOpen,
		setIsCreateRosterModalOpen
	] = useState(false);
	const [
		isDeleteRosterModalOpen,
		setIsDeleteRosterModalOpen
	] = useState(false);
	const [
		rosterToDelete,
		setRosterToDelete
	] = useState(null);
	const [
		isRostedCreated,
		setIsRosterCreated
	] = useState(false);
	const [
		isRostedDeleted,
		setIsRosterDeleted
	] = useState(false);
	const [
		editingRoster,
		setEditingRoster
	] = useState(null);
	const [
		rosterTeamName,
		setRosterTeamName
	] = useState('');
	const [
		rosterLeagueName,
		setRosterLeagueName
	] = useState('');
	const [
		rosterPlatform,
		setRosterPlatform
	] = useState('');
	const [
		rosterUsername,
		setRosterUsername
	] = useState('');
	const [
		rosterUrl,
		setRosterUrl
	] = useState('');
	const [
		rosterFormat,
		setRosterFormat
	] = useState('');
	const [
		rosterScoring,
		setRosterScoring
	] = useState('');
	const [
		rosterChampionshipYears,
		setRosterChampionshipYears
	] = useState([]);
	const [
		additionalEmails,
		setAdditionalEmails
	] = useState([
		user.email
	]);
	const [
		errors,
		setErrors
	] = useState({});

	// Injuries
	const [
		rostersPlayersInjuries,
		setRostersPlayersInjuries
	] = useState([]);
	const [
		isInjuriesModalOpen,
		setIsInjuriesModalOpen
	] = useState(false);

	const handleOpenInjuriesModal = () => {
		handleFetchRostersPlayersInjuriesByUser();
		setIsInjuriesModalOpen(true);
	};

	const handleCloseInjuriesModal = () => {
		setIsInjuriesModalOpen(false);
	};

	const closeInjuriesModalOnClickOutside = (event) => {
		if (modalRef.current && !modalRef.current.contains(event.target)) {
			setIsInjuriesModalOpen(false);
		}
	};

	const handleFetchRostersPlayersInjuriesByUser = async () => {
		await fetchRostersPlayersInjuriesByUser(); // Hack: Ensure this completes first. Ideally, refactor to avoid this call altogether
	};

	const [
		isInjuriesLoading,
		setIsInjuriesLoading
	] = useState(false);

	// Exposures
	const [
		rostersPlayersExposures,
		setRostersPlayersExposures
	] = useState([]);
	const [
		isExposuresModalOpen,
		setIsExposuresModalOpen
	] = useState(false);

	const handleOpenExposuresModal = () => {
		handleFetchRostersPlayersExposuresByUser();
		setIsExposuresModalOpen(true);
	};

	const handleCloseExposuresModal = () => {
		setIsExposuresModalOpen(false);
	};

	const closeExposuresModalOnClickOutside = (event) => {
		if (modalRef.current && !modalRef.current.contains(event.target)) {
			setIsExposuresModalOpen(false);
		}
	};

	const handleFetchRostersPlayersExposuresByUser = async () => {
		await fetchRostersPlayersExposuresByUser(); // Hack: Ensure this completes first. Ideally, refactor to avoid this call altogether
		checkExposureReportAccessEligibility();
	};

	const [
		isExposuresLoading,
		setIsExposuresLoading
	] = useState(false);
	const [
		isChartView,
		setIsChartView
	] = useState(true);

	const handleShowChartView = () => {
		setIsChartView(true);
	};

	const handleShowTableView = () => {
		setIsChartView(false);
	};

	// Filter and sort state variables
	const [
		filterByPlatform,
		setFilterByPlatform
	] = useState({ platform: '' });
	const [
		filterByScoringType,
		setFilterByScoringType
	] = useState({ scoringType: '' });
	const [
		filterByFormat,
		setFilterByFormat
	] = useState({ format: '' });
	const [
		sortBy,
		setSortBy
	] = useState('format');
	const [
		sortOrder,
		setSortOrder
	] = useState('asc');
	const [
		search,
		setSearch
	] = useState('');
	const [
		successModalVisible,
		setSuccessModalVisible
	] = useState(false);

	const closeSuccessModal = () => {
		setSuccessModalVisible(false);
		setIsRosterCreated(false);
		setIsRosterDeleted(false);
	};

	useEffect(
		() => {
			fetchRostersByUser();
		},
		[
			filterByPlatform,
			filterByScoringType,
			filterByFormat,
			sortBy,
			sortOrder,
			search
		]
	);

	const clearErrors = () => {
		setErrors({});
	};

	// Pricing Table
	const [
		isPricingTableModalOpen,
		setIsPricingTableModalOpen
	] = useState(false);

	const fetchRostersByUser = () => {
		window.scrollTo(0, 0);

		if (get_rosters_by_user_endpoint && user) {
			let endpoint = get_rosters_by_user_endpoint.replace('user_id', user.id);
			const params = new URLSearchParams({
				sortBy: sortBy,
				sortOrder: sortOrder,
				platform: filterByPlatform.platform || '',
				scoringType: filterByScoringType.scoringType || '',
				format: filterByFormat.format || '',
				query: search || ''
			});
			endpoint += `?${params.toString()}`;

			fetch(endpoint)
				.then((response) => response.json())
				.then((data) => {
					setRosters(data.data || []);
				})
				.catch((error) => {
					console.error('Error fetching rosters:', error);
				});
		}
	};

	const fetchRostersPlayersExposuresByUser = () => {
		if (get_rosters_players_exposures_by_user_endpoint && user) {
			let endpoint = get_rosters_players_exposures_by_user_endpoint.replace(
				'user_id',
				user.id
			);
			const params = new URLSearchParams({
				platform: filterByPlatform.platform || '',
				scoringType: filterByScoringType.scoringType || '',
				format: filterByFormat.format || ''
			});
			endpoint += `?${params.toString()}`;
			setIsExposuresLoading(true);

			fetch(endpoint)
				.then((response) => response.json())
				.then((data) => {
					setRostersPlayersExposures(data || []);
					setIsExposuresLoading(false);
				})
				.catch((error) => {
					console.error('Error fetching rosters players exposures:', error);
				});
		}
	};

	const fetchRostersPlayersInjuriesByUser = () => {
		if (get_rosters_players_injuries_by_user_endpoint && user) {
			let endpoint = get_rosters_players_injuries_by_user_endpoint.replace(
				'user_id',
				user.id
			);
			const params = new URLSearchParams({
				platform: filterByPlatform.platform || '',
				scoringType: filterByScoringType.scoringType || '',
				format: filterByFormat.format || ''
			});
			endpoint += `?${params.toString()}`;
			setIsInjuriesLoading(true);

			fetch(endpoint)
				.then((response) => response.json())
				.then((data) => {
					setRostersPlayersInjuries(data || []);
					setIsInjuriesLoading(false);
				})
				.catch((error) => {
					console.error('Error fetching rosters players injuries:', error);
				});
		}
	};

	const checkRosterCreationEligibility = () => {
		if (!subscription || Object.keys(subscription).length === 0) {
			setIsPricingTableModalOpen(true);
			setIsCreateRosterModalOpen(false);
			setIsDeleteRosterModalOpen(false);
			return;
		} else if (subscription && subscription.status !== 'active') {
			setIsPricingTableModalOpen(true);
			setIsCreateRosterModalOpen(false);
			setIsDeleteRosterModalOpen(false);
			return;
		} else if (
			rosters &&
			rosters.length >= 5 &&
			subscription &&
			subscription.status === 'active' &&
			subscription.product_name === 'Essential Fantasy Fitness'
		) {
			setIsPricingTableModalOpen(true);
			setIsCreateRosterModalOpen(false);
			return;
		} else if (
			rosters &&
			rosters.length >= 20 &&
			subscription &&
			subscription.status === 'active' &&
			subscription.product_name === 'Advanced Fantasy Fitness'
		) {
			setIsPricingTableModalOpen(true);
			setIsCreateRosterModalOpen(false);
			return;
		} else if (
			rosters &&
			rosters.length >= 100 &&
			subscription &&
			subscription.status === 'active' &&
			subscription.product_name === 'Ultimate Fantasy Fitness'
		) {
			setIsPricingTableModalOpen(true);
			setIsCreateRosterModalOpen(false);
			return;
		} else return;
	};

	const checkRosterEditEligibility = () => {
		if (!subscription || Object.keys(subscription).length === 0) {
			setIsPricingTableModalOpen(true);
			setIsCreateRosterModalOpen(false); // Need to be renamed to setIsEditRosterModalOpen as this is more accepetable for create and edit
			setIsDeleteRosterModalOpen(false);
			return;
		} else if (subscription && subscription.status !== 'active') {
			setIsPricingTableModalOpen(true);
			setIsCreateRosterModalOpen(false); // Need to be renamed to setIsEditRosterModalOpen as this is more accepetable for create and edit
			setIsDeleteRosterModalOpen(false);
			return;
		} else if (
			subscription &&
			subscription.status === 'active' &&
			subscription.product_name === 'Essential Fantasy Fitness'
		) {
			setIsCreateRosterModalOpen(true);
			return;
		} else if (
			subscription &&
			subscription.status === 'active' &&
			subscription.product_name === 'Advanced Fantasy Fitness'
		) {
			setIsCreateRosterModalOpen(true);
			return;
		} else if (
			subscription &&
			subscription.status === 'active' &&
			subscription.product_name === 'Ultimate Fantasy Fitness'
		) {
			setIsCreateRosterModalOpen(true);
			return;
		} else return;
	};

	const checkExposureReportAccessEligibility = () => {
		if (
			!subscription ||
			Object.keys(subscription).length === 0 ||
			subscription.status !== 'active'
		) {
			setIsPricingTableModalOpen(true);
			setIsExposuresModalOpen(false);
			return;
		}

		if (!rosters || rosters.length === 0) {
			setIsPricingTableModalOpen(true);
			setIsExposuresModalOpen(false);
			return;
		}

		if (subscription.product_name === 'Essential Fantasy Fitness') {
			setIsPricingTableModalOpen(true);
			setIsExposuresModalOpen(false);
			return;
		}

		if (!isExposuresModalOpen) {
			setIsExposuresModalOpen(true);
		}
	};

	const handleEditClick = (roster) => {
		setEditingRoster(roster);
		setRosterTeamName(roster.team_name);
		setRosterLeagueName(
			roster.league && roster.league.league_name
				? roster.league.league_name
				: ''
		);
		setRosterPlatform(
			roster.league && roster.league.platform ? roster.league.platform : ''
		);
		setRosterUsername(roster.username);
		setRosterUrl(roster.roster_url);
		setRosterFormat(
			roster.league && roster.league.format ? roster.league.format : ''
		);
		setRosterScoring(
			roster.league && roster.league.scoring ? roster.league.scoring : ''
		);
		setRosterChampionshipYears(roster.championship_years);
		setAdditionalEmails(roster.users.map((user) => user.email));
		setIsCreateRosterModalOpen(true);
		checkRosterEditEligibility(); // Hack: Setting at end to ensure it runs last but I don't like this and think it should be refactored
	};

	const handleCreateClick = () => {
		setEditingRoster(null); // Reset editing state
		setIsCreateRosterModalOpen(true);
		setRosterTeamName('');
		setRosterLeagueName('');
		setRosterPlatform('');
		setRosterUsername('');
		setRosterUrl('');
		setRosterFormat('');
		setRosterScoring('');
		setRosterChampionshipYears([]);
		setAdditionalEmails([
			user.email
		]);
		checkRosterCreationEligibility(); // Hack: Setting at end to ensure it runs last but I don't like this and think it should be refactored
	};

	const closeRosterCreateModal = () => {
		setIsCreateRosterModalOpen(false);
		setEditingRoster(null);
	};

	const closeRosterDeleteModal = () => {
		setIsDeleteRosterModalOpen(false);
		setRosterToDelete(null);
	};

	const handleClickOutsideRosterDeleteModal = (event) => {
		if (modalRef.current && !modalRef.current.contains(event.target)) {
			closeRosterDeleteModal();
		}
	};

	useEffect(
		() => {
			if (isDeleteRosterModalOpen) {
				document.addEventListener(
					'mousedown',
					handleClickOutsideRosterDeleteModal
				);
			} else {
				document.removeEventListener(
					'mousedown',
					handleClickOutsideRosterDeleteModal
				);
			}
			return () => {
				document.removeEventListener(
					'mousedown',
					handleClickOutsideRosterDeleteModal
				);
			};
		},
		[
			isDeleteRosterModalOpen
		]
	);

	useEffect(
		() => {
			if (isCreateRosterModalOpen || isDeleteRosterModalOpen) {
				document.body.style.overflow = 'hidden';
			} else {
				document.body.style.overflow = 'auto';
			}
		},
		[
			isCreateRosterModalOpen,
			isDeleteRosterModalOpen
		]
	);

	useEffect(
		() => {
			if (isExposuresModalOpen) {
				document.addEventListener(
					'mousedown',
					closeExposuresModalOnClickOutside
				);
			} else {
				document.removeEventListener(
					'mousedown',
					closeExposuresModalOnClickOutside
				);
			}
			return () => {
				document.removeEventListener(
					'mousedown',
					closeExposuresModalOnClickOutside
				);
			};
		},
		[
			isExposuresModalOpen
		]
	);
	useEffect(
		() => {
			if (isInjuriesModalOpen) {
				document.addEventListener(
					'mousedown',
					closeInjuriesModalOnClickOutside
				);
			} else {
				document.removeEventListener(
					'mousedown',
					closeInjuriesModalOnClickOutside
				);
			}
			return () => {
				document.removeEventListener(
					'mousedown',
					closeInjuriesModalOnClickOutside
				);
			};
		},
		[
			isInjuriesModalOpen
		]
	);

	useEffect(
		() => {
			if (isExposuresModalOpen || isInjuriesModalOpen) {
				document.body.style.overflow = 'hidden';
			}

			return () => {
				document.body.style.overflow = 'auto';
			};
		},
		[
			isExposuresModalOpen,
			isInjuriesModalOpen
		]
	);

	const handleSaveRoster = (navigateToNewRoster = false) => {
		const payload = {
			user_id: user.id,
			roster: {
				team_name: rosterTeamName,
				league_name: rosterLeagueName,
				platform: rosterPlatform,
				username: rosterUsername,
				roster_url: rosterUrl,
				format: rosterFormat,
				scoring: rosterScoring,
				championship_years: rosterChampionshipYears
			},
			emails: additionalEmails,
			filter_by_platform: filterByPlatform.platform,
			filter_by_scoring_type: filterByScoringType.scoringType,
			filter_by_format: filterByFormat.format,
			sort_by: sortBy,
			sort_order: sortOrder
		};

		const endpoint = editingRoster
			? update_roster_by_user_endpoint
					.replace('user_id', user.id)
					.replace('roster_id', editingRoster.id)
			: create_roster_by_user_endpoint.replace('user_id', user.id);

		const method = editingRoster ? 'PUT' : 'POST';

		const csrfToken = document.querySelector('[name="csrf-token"]').content;

		fetch(endpoint, {
			method: method,
			headers: {
				'Content-Type': 'application/json',
				'X-CSRF-Token': csrfToken
			},
			body: JSON.stringify(payload)
		})
			.then((response) => response.json())
			.then((data) => {
				if (data.success) {
					setRosters(data.data);
					setErrors({});
					// console.log(
					//  editingRoster ? 'Roster updated:' : 'Roster created:',
					//  data.data
					// );
					if (navigateToNewRoster) {
						const newOrUpdatedRosterId = data.new_or_updated_roster.id;
						navigate(`/rosters/${newOrUpdatedRosterId}`);
					}
					fetchRostersByUser();
					closeRosterCreateModal();
					setIsRosterCreated(editingRoster ? false : true);
					setSuccessModalVisible(true);
				} else {
					console.error(
						editingRoster
							? 'Failed to update roster:'
							: 'Failed to create roster:',
						data.message
					);
					setErrors(data.field_errors || {});
				}
			})
			.catch((error) => {
				console.error('Error:', error);
			});
	};

	const handleDeleteRoster = () => {
		const endpoint = delete_roster_by_user_endpoint
			.replace('user_id', user.id)
			.replace('roster_id', rosterToDelete.id);

		const queryParams = new URLSearchParams({
			filter_by_platform: filterByPlatform.platform,
			filter_by_scoring_type: filterByScoringType.scoringType,
			filter_by_format: filterByFormat.format,
			sort_by: sortBy,
			sort_order: sortOrder
		});

		fetch(`${endpoint}?${queryParams.toString()}`, {
			method: 'DELETE'
		})
			.then((response) => response.json())
			.then((data) => {
				if (data.success) {
					setRosters(data.data);
					fetchRostersByUser();
					closeRosterDeleteModal();
					setIsRosterDeleted(true);
					setSuccessModalVisible(true);
				} else {
					console.error('Failed to delete roster:', data.message);
				}
			})
			.catch((error) => {
				console.error('Error:', error);
			});
	};

	const confirmDeleteRoster = (roster) => {
		if (user.id === roster.primary_user_id) {
			setRosterToDelete(roster);
			setIsDeleteRosterModalOpen(true);
			return;
		}

		console.log(
			'You are not the primary user of this roster. Only the primary user can delete this roster.'
		);
	};

	const handleEmailChange = (index, value) => {
		const updatedEmails = [
			...additionalEmails
		];
		updatedEmails[index] = value;
		setAdditionalEmails(updatedEmails);
	};

	const handleAddEmail = () => {
		setAdditionalEmails([
			...additionalEmails,
			''
		]);
	};

	const handleRemoveEmail = (index) => {
		const updatedEmails = [
			...additionalEmails
		];
		updatedEmails.splice(index, 1);
		setAdditionalEmails(updatedEmails);
	};

	const closePricingTableModal = () => {
		setIsCreateRosterModalOpen(false);
		setIsPricingTableModalOpen(false);
	};

	const closePricingTableModalOnClickOutside = (event) => {
		if (modalRef.current && !modalRef.current.contains(event.target)) {
			setIsCreateRosterModalOpen(false);
			setIsPricingTableModalOpen(false);
		}
	};

	useEffect(
		() => {
			if (isPricingTableModalOpen) {
				document.addEventListener(
					'mousedown',
					closePricingTableModalOnClickOutside
				);
			} else {
				document.removeEventListener(
					'mousedown',
					closePricingTableModalOnClickOutside
				);
			}
			return () => {
				document.removeEventListener(
					'mousedown',
					closePricingTableModalOnClickOutside
				);
			};
		},
		[
			isPricingTableModalOpen
		]
	);

	useEffect(
		() => {
			if (isPricingTableModalOpen) {
				document.body.style.overflow = 'hidden';
			} else {
				document.body.style.overflow = 'auto';
			}
		},
		[
			isPricingTableModalOpen
		]
	);

	// Filter and sort state variables
	const handleFilterChange = (filterKey, value) => {
		switch (filterKey) {
			case 'format':
				setFilterByFormat({ format: value });
				break;
			case 'platform':
				setFilterByPlatform({ platform: value });
				break;
			case 'scoringType':
				setFilterByScoringType({ scoringType: value });
				break;
			default:
				break;
		}
	};

	const handleSearchInputChange = (event) => {
		setSearch(event.target.value);
		fetchRostersByUser();
	};

	const handleSortOrderChange = (sortColumn, sortOrder) => {
		if (sortOrder === '' || sortOrder === 'desc') {
			setSortBy(sortColumn);
			setSortOrder('asc');
		} else if (sortOrder === 'asc') {
			setSortBy(sortColumn);
			setSortOrder('desc');
		}
	};

	const handleClearFilters = () => {
		setFilterByPlatform({ platform: '' });
		setFilterByScoringType({ scoringType: '' });
		setFilterByFormat({ format: '' });
		setSearch('');
	};

	if (!rosters) {
		return (
			<div>
				<LoadingSpinner />
			</div>
		);
	}

	return (
		<div>
			<div className="flex flex-col lg:flex-row lg:mb-6">
				<div className="md:mr-6">
					<FilterByFormat
						filter={filterByFormat}
						onChange={(filterKey, value) =>
							handleFilterChange(filterKey, value)}
					/>
				</div>
				<div className="md:mr-6">
					<FilterByPlatform
						filter={filterByPlatform}
						onChange={(filterKey, value) =>
							handleFilterChange(filterKey, value)}
					/>
				</div>
				<div className="md:mr-6">
					<FilterByScoringType
						filter={filterByScoringType}
						onChange={(filterKey, value) =>
							handleFilterChange(filterKey, value)}
					/>
				</div>
				<div className="md:mr-6">
					<FilterBySearchInput
						label="Filter By Team or League"
						value={search}
						placeholder="Search for a team or league"
						onChange={handleSearchInputChange}
					/>
				</div>
				<div className="hidden lg:contents">
					<ClearFiltersButton onClick={handleClearFilters} />
				</div>
			</div>
			<div className="p-6 lg:p-8 border border-gray-300 dark:border-dark-border rounded-xl shadow-lg bg-white dark:bg-dark-card text-gray-800 dark:text-dark-text">
				{rosters && rosters.length === 0 ? (
					<div className="text-center">
						<p>No results found. Create a new roster to get started.</p>
						<button
							className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
							onClick={handleCreateClick}
						>
							Create roster
						</button>
					</div>
				) : (
					<Fragment>
						<div className="contents lg:hidden">
							<MobileDataTableCard
								data={rosters}
								style="text-gray-800 dark:text-dark-text border-b border-gray-300 dark:border-dark-border font-semibold"
								rowRenderer={(row) => {
									const labels = [
										{
											displayName: 'Team',
											apiName: 'team_name',
											alignment: 'left'
										}
									];
									return (
										<div
											key={`${row.id} - RostersIndexCard - MobileDataTableCard`}
											className="p-5 bg-white dark:bg-dark-card border-t border-gray-200 dark:border-dark-border text-gray-800 dark:text-dark-text"
										>
											<div className="flex justify-between items-center">
												<Link
													to={`/rosters/${row.id}`}
													className="text-blue-500 hover:text-blue-700"
												>
													<div
														style={{ display: 'flex', alignItems: 'center' }}
													>
														{`${row.team_name}`}
													</div>
												</Link>
												<div className="flex items-center">
													<span className="text-sm text-gray-600 dark:text-gray-300 mr-4">
														Record: {row.record || 'N/A'}
													</span>
													<button
														className="text-sm text-blue-500 hover:underline mr-4"
														onClick={() => handleEditClick(row)}
													>
														Edit
													</button>
													<button
														className="text-sm text-blue-500 hover:underline"
														onClick={() => confirmDeleteRoster(row)}
													>
														Delete
													</button>
												</div>
											</div>
										</div>
									);
								}}
							/>
						</div>
						<div className="hidden lg:contents">
							<AdvancedDataTable
								data={rosters}
								style="text-gray-800 dark:text-dark-text border-b border-gray-300 dark:border-dark-border font-semibold"
								sortOrder={sortOrder}
								sortColumn={sortBy ? sortBy : ''}
								onChangeSortOrder={handleSortOrderChange}
								sortingEnabled={true}
								columns={[
									{
										displayName: 'Team',
										apiName: 'team_name',
										alignment: 'left'
									},
									{
										displayName: 'League',
										apiName: 'league_name',
										alignment: 'left'
									},
									{
										displayName: 'Platform',
										apiName: 'platform',
										alignment: 'left'
									},
									{
										displayName: 'Format',
										apiName: 'format',
										alignment: 'left'
									},
									{
										displayName: 'Scoring',
										apiName: 'scoring',
										alignment: 'left'
									},
									{
										displayName: 'Record',
										apiName: 'record',
										alignment: 'left'
									},
									{ displayName: 'Actions', alignment: 'right' }
								]}
								rowRenderer={(row) => (
									<tr key={row.id} className="bg-white dark:bg-dark-card border-t border-gray-200 dark:border-dark-border text-gray-800 dark:text-dark-text">
										<td className="pl-8 py-3 text-left">
											<Link
												className="text-blue-500 hover:underline cursor:pointer"
												to={`/rosters/${row.id}`}
											>
												{row.team_name}
											</Link>
										</td>
										<td className="pl-8 py-3 text-left">
											{row.league && row.league.league_name ? (
												row.league.league_name
											) : (
												'N/A'
											)}
										</td>
										<td className="pl-8 py-3 text-left">
											<a
												className="text-blue-500 hover:underline"
												href={getRosterPlatformUrl(
													row.league && row.league.platform
														? row.league.platform
														: null,
													row.roster_url
												)}
												target="_blank"
												rel="noreferrer"
											>
												{row.league && row.league.platform ? (
													row.league.platform
												) : (
													'N/A'
												)}
												<i className="fa-solid fa-arrow-up-right-from-square ml-2 text-blue-500" />
											</a>
										</td>
										<td className="pl-8 py-3 text-left">
											{row.league && row.league.format ? (
												row.league.format
											) : (
												'N/A'
											)}
										</td>
										<td className="pl-8 py-3 text-left">
											{row.league && row.league.scoring ? (
												row.league.scoring
											) : (
												'N/A'
											)}
										</td>
										<td className="pl-8 py-3 text-left">
											{row.record || 'N/A'}
										</td>
										<td className="pl-8 py-3 text-right">
											<button
												className="text-sm text-blue-500 hover:underline mr-4"
												onClick={() => handleEditClick(row)}
											>
												Edit
											</button>
											<button
												className={`text-sm mr-4 ${user.id ===
												row.primary_user_id
													? 'text-blue-500 hover:underline'
													: 'text-gray-400'}`}
												onClick={() => {
													if (user.id === row.primary_user_id) {
														confirmDeleteRoster(row);
													}
												}}
												disabled={user.id !== row.primary_user_id}
												title={
													user.id !== row.primary_user_id ? (
														'Only the primary user can delete this roster'
													) : (
														undefined
													)
												}
											>
												Delete
											</button>
										</td>
									</tr>
								)}
							/>
						</div>
						<div className="text-center my-6 lg:mb-0">
							<button
								className="mx-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
								onClick={handleCreateClick}
							>
								Create roster
							</button>
							<button
								className="mx-2 mt-4 md:mt-0 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
								onClick={handleOpenExposuresModal}
							>
								View players exposures
							</button>
							<button
								className="mx-2 mt-4 md:mt-0 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
								onClick={handleOpenInjuriesModal}
							>
								View players injuries
							</button>
						</div>
					</Fragment>
				)}

				{/* Create/Edit Roster Modal */}
				{isCreateRosterModalOpen && (
					<RosterFormModal
						isOpen={isCreateRosterModalOpen}
						modalRef={modalRef}
						closeModal={closeRosterCreateModal}
						editingRoster={editingRoster}
						handleSaveRoster={handleSaveRoster}
						rosterTeamName={rosterTeamName}
						setRosterTeamName={setRosterTeamName}
						rosterLeagueName={rosterLeagueName}
						setRosterLeagueName={setRosterLeagueName}
						rosterPlatform={rosterPlatform}
						setRosterPlatform={setRosterPlatform}
						rosterUsername={rosterUsername}
						setRosterUsername={setRosterUsername}
						rosterUrl={rosterUrl}
						setRosterUrl={setRosterUrl}
						rosterFormat={rosterFormat}
						setRosterFormat={setRosterFormat}
						rosterScoring={rosterScoring}
						setRosterScoring={setRosterScoring}
						rosterChampionshipYears={rosterChampionshipYears}
						setRosterChampionshipYears={setRosterChampionshipYears}
						additionalEmails={additionalEmails}
						handleEmailChange={handleEmailChange}
						handleAddEmail={handleAddEmail}
						handleRemoveEmail={handleRemoveEmail}
						errors={errors}
						clearErrors={clearErrors}
					/>
				)}

				{/* Delete Roster Modal */}
				{isDeleteRosterModalOpen && (
					<div
						className="fixed inset-0 z-50 overflow-auto flex w-full"
						style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
					>
						<div
							className="relative p-8 bg-white dark:bg-dark-card m-auto flex-col flex rounded-lg border border-transparent dark:border-dark-border"
							ref={modalRef}
						>
							<p className="mb-4 text-gray-900 dark:text-dark-text">
								Are you sure you want to delete the roster, "{rosterToDelete.team_name}"?
							</p>
							<div className="flex justify-end">
								<button
									className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 dark:bg-dark-border dark:text-dark-text dark:hover:bg-gray-700 mr-4"
									onClick={closeRosterDeleteModal}
								>
									Cancel
								</button>
								<button
									className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-500"
									onClick={handleDeleteRoster}
								>
									Yes, delete
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Exposures Modal */}
				{isExposuresModalOpen && (
					<Modal onClose={handleCloseExposuresModal} modalRef={modalRef}>
						{isExposuresLoading ? null : (
							<div className="relative">
								<h2 className="text-xl font-bold mb-4">
									Players Exposures Overview
								</h2>
								<div className="absolute right-0">
									<button
										onClick={
											isChartView ? handleShowTableView : handleShowChartView
										}
										className="p-2 text-gray-500 hover:text-blue-500 transition-colors"
									>
										{isChartView ? (
											<i className="fa-solid fa-list" />
										) : (
											<i className="fa-solid fa-chart-column" />
										)}
									</button>
								</div>
							</div>
						)}
						{isChartView ? (
							<RostersPlayersExposuresChart
								isLoading={isExposuresLoading}
								data={rostersPlayersExposures.exposure_data}
								totalRosters={rostersPlayersExposures.total_rosters}
								format={filterByFormat.format}
								platform={filterByPlatform.platform}
								scoringType={filterByScoringType.scoringType}
							/>
						) : (
							<RostersPlayersExposuresTable
								isLoading={isExposuresLoading}
								data={rostersPlayersExposures.exposure_data}
								totalRosters={rostersPlayersExposures.total_rosters}
								format={filterByFormat.format}
								platform={filterByPlatform.platform}
								scoringType={filterByScoringType.scoringType}
							/>
						)}
					</Modal>
				)}
				{/* Injuries Modal */}
				{isInjuriesModalOpen && (
					<Modal onClose={handleCloseInjuriesModal} modalRef={modalRef}>
						{isInjuriesLoading ? null : (
							<Fragment>
								<div className="relative">
									<h2 className="text-xl font-bold mb-4">
										Rosters Injury Summary
									</h2>
									<div className="py-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
										<div className="text-base text-gray-500 dark:text-gray-300 flex flex-col sm:flex-row gap-4 sm:gap-8 text-center sm:text-left">
											<div className="flex items-center">
												<span className="font-bold uppercase">Format</span>
												<span className="ml-2">
													{filterByFormat.format ? filterByFormat.format : 'All'}
												</span>
											</div>
											<div className="flex items-center">
												<span className="font-bold uppercase">Platform</span>
												<span className="ml-2">
													{filterByPlatform.platform ? (
														filterByPlatform.platform
													) : (
														'All'
													)}
												</span>
											</div>
											<div className="flex items-center">
												<span className="font-bold uppercase">Scoring</span>
												<span className="ml-2">
													{filterByScoringType.scoringType ? (
														filterByScoringType.scoringType
													) : (
														'All'
													)}
												</span>
											</div>
											<div className="flex items-center">
												<span className="font-bold uppercase">
													Total Rosters
												</span>
												<span className="ml-2">
													{rostersPlayersInjuries.total_rosters ? (
														rostersPlayersInjuries.total_rosters
													) : (
														'Contact Support'
													)}
												</span>
											</div>
										</div>
										<Link
											to="/injury-report?scope=mine"
											className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
											onClick={handleCloseInjuriesModal}
										>
											Open full Injury Report
										</Link>
									</div>
									<div className="mt-4 space-y-4">
										{rostersPlayersInjuries.injury_data.length === 0 ? (
											<p className="text-gray-500">No injuries reported.</p>
										) : (
											rostersPlayersInjuries.injury_data.map((roster) => (
												<div
													key={roster.roster.id}
													className="p-4 rounded-md border border-gray-300"
												>
													<h3 className="text-lg font-semibold mb-2 underline text-gray-800 dark:text-dark-text inline-block">
														{roster.roster.team_name}
													</h3>
													<span className="text-gray-500 dark:text-gray-300 ml-2">
														({roster.roster.league &&
														roster.roster.league.league_name ? (
															roster.roster.league.league_name
														) : (
															'N/A'
														)})
													</span>
													<ul className="list-disc pl-5">
														{roster.injuries.length === 0 ? (
															<li className="text-gray-800 dark:text-dark-text">
																No injuries reported
															</li>
														) : (
															roster.injuries.map((injury, index) => (
																<li key={index} className="text-gray-800 dark:text-dark-text">
																	{injury.player_name} -{' '}
																	<span className="text-gray-500 dark:text-gray-300">
																		{injury.status}
																		{injury.injury_description ? (
																			`, ${injury.injury_description}`
																		) : (
																			''
																		)}
																	</span>
																</li>
															))
														)}
													</ul>
												</div>
											))
										)}
									</div>
								</div>
							</Fragment>
						)}
					</Modal>
				)}

				{/* Pricing Table Modal */}
				{isPricingTableModalOpen && (
					<div
						className="fixed inset-0 z-50 flex justify-center items-center"
						style={{
							backgroundColor: 'rgba(0, 0, 0, 0.5)'
						}}
					>
						<div
							ref={modalRef}
							className="relative bg-white rounded-lg p-6 shadow-lg"
							style={{
								width: 'auto',
								height: 'auto',
								maxHeight: '90%',
								overflowY: 'auto'
							}}
						>
							<button
								className="absolute top-0 right-0 p-4"
								onClick={closePricingTableModal}
							>
								<svg
									className="h-6 w-6 text-gray-500"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
							<h2 className="text-2xl font-bold mb-4">Our Pricing</h2>
							<div className="overflow-x-auto">
								<div className="flex flex-col block" style={{ height: '60%' }}>
									<PricingTable
										create_checkout_session_endpoint={
											create_checkout_session_endpoint
										}
										stripe_public_key={stripe_public_key}
										stripe_product_essential_id={stripe_product_essential_id}
										stripe_product_advanced_id={stripe_product_advanced_id}
										stripe_product_ultimate_id={stripe_product_ultimate_id}
									/>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Success Modal */}
				<SuccessModal
					visible={successModalVisible}
					onClose={closeSuccessModal}
					isRostedDeleted={isRostedDeleted}
					message={
						isRostedDeleted ? (
							'Roster deleted successfully.'
						) : isRostedCreated ? (
							'Roster created successfully.'
						) : (
							''
						)
					}
				/>
			</div>
		</div>
	);
};

export default RostersIndexCard;
